import { faArrowLeft, faEdit, faGripLines, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Row, Stack } from "react-bootstrap";
import { deleteProgramLevel, patchProgram, patchProgramLevel, postProgramLevel, reorderProgramLevels } from "../../../services/httpClient";
import { unsavedChanges } from "../../../App";
import Input from "../../ui/Input";
import IconButton from "../../ui/IconButton";
import ProgramLevelCard, { CatalogOption } from "./ProgramLevelCard";
import { isPathwayProgram } from "./ProgramCard";

interface EditorState {
  program: any;
  getItems: any;
  onDone: () => void;
  setOrgToSave: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  goalTemplateCatalog: CatalogOption[];
  assessmentTypeCatalog: CatalogOption[];
  noteTypeCatalog: CatalogOption[];
}

// Full-page editor for one program: its levels on the left, the selected level on the right.
// Every change saves as it is made; Done just returns to the list.
const ProgramEditor = ({
  program,
  getItems,
  onDone,
  setOrgToSave,
  setErrMessage,
  setShowToast,
  setToastText,
  setToastBody,
  goalTemplateCatalog,
  assessmentTypeCatalog,
  noteTypeCatalog,
}: EditorState) => {
  const [levels, setLevels] = useState<any[]>(program.programLevels || []);
  const [selectedId, setSelectedId] = useState<any>(program.programLevels?.[0]?.id);
  const [isRenaming, setIsRenaming] = useState(false);
  const [progName, setProgName] = useState<string>(program.name);
  const [isAdding, setIsAdding] = useState(false);
  const [newLevel, setNewLevel] = useState<string>("");
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  useEffect(() => {
    setLevels(program.programLevels || []);
    setProgName(program.name);
  }, [program]);

  // keep a valid level selected as levels are added or removed
  useEffect(() => {
    if (!levels.some((l: any) => l.id === selectedId)) {
      setSelectedId(levels[0]?.id);
    }
  }, [levels]);

  const selected = levels.find((l: any) => l.id === selectedId);

  const toast = (title: string, body: string) => {
    setShowToast(true);
    setToastText(title);
    setToastBody(body);
  };

  const onError = (err: any) => {
    setErrMessage(err?.response?.data?.msg);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const onClickSaveName = () => {
    patchProgram({ name: progName }, program.id)
      .then(() => {
        unsavedChanges.value = false;
        setOrgToSave(false);
        setIsRenaming(false);
        toast("Program Updated", progName);
        getItems(true);
      })
      .catch(onError);
  };

  const onClickCancelName = () => {
    unsavedChanges.value = false;
    setOrgToSave(false);
    setIsRenaming(false);
    setProgName(program.name);
    setErrMessage(undefined);
  };

  const onClickSaveLevel = () => {
    setErrMessage("");
    if (!newLevel || newLevel.trim() === "") {
      setErrMessage("Please enter a name for the program level.");
      return;
    }
    postProgramLevel(program.id, newLevel)
      .then(() => {
        unsavedChanges.value = false;
        setOrgToSave(false);
        toast("Program Level Created", newLevel);
        setIsAdding(false);
        setNewLevel("");
        getItems(true);
      })
      .catch(onError);
  };

  const onClickCancelAdd = () => {
    unsavedChanges.value = false;
    setOrgToSave(false);
    setIsAdding(false);
    setErrMessage("");
    setNewLevel("");
  };

  const onEditLevelFinal = (levelId: any, levelName: string) => {
    setErrMessage("");
    patchProgramLevel(levelId, levelName)
      .then(() => {
        unsavedChanges.value = false;
        setOrgToSave(false);
        toast("Program Level Updated", levelName);
        getItems(true);
      })
      .catch((err: any) => setErrMessage(err?.response?.data?.msg));
  };

  const onDeleteLevelFinal = (levelId: any, name: string) => {
    deleteProgramLevel(levelId)
      .then(() => {
        toast("Program Level Deleted", name);
        getItems(true);
      })
      .catch((err: any) => setErrMessage(err?.response?.data?.msg));
  };

  const handleDrop = (dropIndex: number) => {
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    const reordered = [...levels];
    const [moved] = reordered.splice(draggedIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setLevels(reordered);
    reorderProgramLevels(reordered.map((l: any) => l.id))
      .then(() => {
        toast("Program Level Order", "The level order of " + program.name + " has been updated.");
        getItems(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
        getItems(true);
      });
  };

  const itemCount = (l: any) => (l.goalTemplates?.length || 0) + (l.assessmentTypes?.length || 0) + (l.noteTypes?.length || 0);

  const renderLevelList = () => (
    <Card className="bg-light">
      <Card.Body className="p-2">
        {levels.map((l: any, index: number) => (
          <div
            key={l.id}
            draggable
            data-testid={`editor-level-${l.id}`}
            onDragStart={(e) => {
              setDraggedIndex(index);
              e.currentTarget.classList.add("dragging");
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (draggedIndex !== null && draggedIndex !== index) setDropTargetIndex(index);
            }}
            onDragLeave={() => setDropTargetIndex(null)}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(index);
              setDropTargetIndex(null);
              setDraggedIndex(null);
            }}
            onDragEnd={(e) => {
              e.currentTarget.classList.remove("dragging");
              setDraggedIndex(null);
              setDropTargetIndex(null);
            }}
            onClick={() => setSelectedId(l.id)}
            className={
              "d-flex align-items-center gap-2 rounded p-2 cursor-pointer" +
              (l.id === selectedId ? " bg-white border fw-bold" : "") +
              (dropTargetIndex === index ? " drop-target" : "")
            }
          >
            <span style={{ cursor: "grab" }} data-tooltip-id="tooltip" data-tooltip-content={"Drag to Reorder"}>
              <FontAwesomeIcon icon={faGripLines} className="text-muted" />
            </span>
            <span className="me-auto text-truncate">{l.name}</span>
            <small className="text-muted">{itemCount(l)}</small>
          </div>
        ))}
        {levels.length === 0 && <p className="text-muted small m-2">No levels yet.</p>}
        {isAdding ? (
          <div className="p-2">
            <Input
              label="Level"
              type="text"
              value={newLevel}
              hasFocus={true}
              onUpdate={(e: any) => {
                setNewLevel(e.target.value);
                unsavedChanges.value = true;
                setOrgToSave(true);
                setErrMessage(undefined);
              }}
              required={true}
            />
            <Stack direction="horizontal" gap={3} className="mt-2">
              <Button className="app-secondary-color button" disabled={!newLevel} onClick={onClickSaveLevel}>
                Save
              </Button>
              <div className="vr" />
              <Button className="app-primary-color button" onClick={onClickCancelAdd}>
                Cancel
              </Button>
            </Stack>
          </div>
        ) : (
          <Button
            className="app-primary-color-inverse button w-100 mt-2"
            onClick={() => {
              setIsAdding(true);
              setErrMessage(undefined);
            }}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Level
          </Button>
        )}
      </Card.Body>
    </Card>
  );

  return (
    <div>
      <Stack direction="horizontal" gap={2} className="cursor-pointer mb-2" onClick={onDone}>
        <FontAwesomeIcon icon={faArrowLeft} className="me-0 mb-1 app-tertiary-color-inverse cursor-pointer" fontSize={16} />
        <h6 className="app-tertiary-color-inverse">Back to Programs</h6>
      </Stack>
      <Row className="mb-4 align-items-center">
        <Col xs={isRenaming ? 12 : 9}>
          {isRenaming ? (
            <Stack direction="horizontal" gap={3}>
              <Col>
                <Input
                  label="Program"
                  type="text"
                  value={progName}
                  hasFocus={true}
                  onUpdate={(e: any) => {
                    setProgName(e.target.value);
                    unsavedChanges.value = true;
                    setOrgToSave(true);
                    setErrMessage(undefined);
                  }}
                />
              </Col>
              <Button className="app-secondary-color button" disabled={!progName?.trim()} onClick={onClickSaveName}>
                Save
              </Button>
              <div className="vr" />
              <Button className="app-primary-color button" onClick={onClickCancelName}>
                Cancel
              </Button>
            </Stack>
          ) : (
            <Stack direction="horizontal" gap={3}>
              <h4 className="app-header-text mb-0">{program.name}</h4>
              <IconButton onClickButton={() => setIsRenaming(true)} tooltipText={"Rename Program"} icon={faEdit} />
              {isPathwayProgram(program) && (
                <Badge bg="none" className="app-secondary-color" style={{ fontSize: "0.75rem" }}>
                  Pathway
                </Badge>
              )}
            </Stack>
          )}
        </Col>
        {!isRenaming && (
          <Col xs={3} className="d-flex justify-content-end">
            <Button className="app-primary-color button" onClick={onDone}>
              Done
            </Button>
          </Col>
        )}
      </Row>
      <Row className="g-3">
        <Col xs={12} md={4} lg={3}>
          {renderLevelList()}
        </Col>
        <Col xs={12} md={8} lg={9}>
          {selected ? (
            <ProgramLevelCard
              key={selected.id}
              level={selected}
              goalTemplateCatalog={goalTemplateCatalog}
              assessmentTypeCatalog={assessmentTypeCatalog}
              noteTypeCatalog={noteTypeCatalog}
              getItems={getItems}
              onEditFinal={onEditLevelFinal}
              onDeleteFinal={onDeleteLevelFinal}
              setErrMessage={setErrMessage}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
            />
          ) : (
            <p className="text-muted">Add a level to start building this program.</p>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ProgramEditor;
